import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, CameraOff } from "lucide-react";
import { toast } from "sonner";

/**
 * ScannerInput — shared reusable component: keyboard + camera capture.
 *
 * Props:
 *  - onScan (required): (code) => void — called for each successful scan
 *  - placeholder (optional)
 *  - testIdPrefix (default "scan")
 *  - autoFocus (default true)
 *  - buttonLabel (default "OK") — text of the confirm button
 *  - dark (default false) — dark input style used inside Inventory
 *
 * Cameras only work on HTTPS or localhost.
 */
export default function ScannerInput({
  onScan,
  placeholder = "",
  testIdPrefix = "scan",
  autoFocus = true,
  buttonLabel = "OK",
  dark = false,
}) {
  const [val, setVal] = useState("");
  const [camOn, setCamOn] = useState(false);
  const camRef = useRef(null);
  const scannerId = `${testIdPrefix}-cam`;

  const stopCam = async () => {
    if (camRef.current) {
      try {
        await camRef.current.stop();
        await camRef.current.clear();
      } catch (e) {
        console.warn("Camera stop failed:", e);
      }
      camRef.current = null;
    }
    setCamOn(false);
  };

  const startCam = () => {
    // Camera requires a secure context (HTTPS) or localhost
    if (typeof window !== "undefined" && window.isSecureContext === false) {
      toast.error("La cámara requiere HTTPS. Abre la app en su URL segura.");
      return;
    }
    if (!navigator?.mediaDevices?.getUserMedia) {
      toast.error("Este navegador no expone la cámara (getUserMedia no disponible)");
      return;
    }
    setCamOn(true);
    setTimeout(async () => {
      try {
        const h = new Html5Qrcode(scannerId);
        camRef.current = h;
        await h.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 130 } },
          async (decoded) => {
            await stopCam();
            onScan(decoded);
          },
          () => {}
        );
      } catch (err) {
        // Common failures: NotAllowedError (permission), NotFoundError (no cam)
        const msg = err?.message || err?.name || String(err);
        toast.error("Cámara: " + msg + ". Verifica los permisos del navegador.");
        setCamOn(false);
      }
    }, 200);
  };

  useEffect(() => () => { stopCam(); }, []);

  const inputCls = dark
    ? "flex-1 min-w-[220px] px-4 py-3 rounded bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 outline-none focus:border-emerald-400 font-mono text-base"
    : "flex-1 min-w-[220px] px-3 py-2 rounded border border-slate-300 outline-none focus:border-red-600 font-mono text-sm";

  return (
    <div>
      <div className="flex gap-2 flex-wrap">
        <input
          data-testid={`${testIdPrefix}-input`}
          autoFocus={autoFocus}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              const v = val.trim();
              if (v) { onScan(v); setVal(""); }
            }
          }}
          placeholder={placeholder}
          className={inputCls}
        />
        <button
          type="button"
          data-testid={`${testIdPrefix}-btn`}
          onClick={() => { const v = val.trim(); if (v) { onScan(v); setVal(""); } }}
          className="bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2 rounded font-semibold text-sm"
        >
          {buttonLabel}
        </button>
        <button
          type="button"
          data-testid={`${testIdPrefix}-cam-toggle`}
          onClick={camOn ? stopCam : startCam}
          className={`${camOn ? "bg-red-500 hover:bg-red-600" : "bg-blue-500 hover:bg-blue-600"} text-white px-4 py-2 rounded font-semibold text-sm flex items-center gap-2`}
        >
          {camOn ? <><CameraOff className="w-4 h-4" /> Stop</> : <><Camera className="w-4 h-4" /> Cámara</>}
        </button>
      </div>
      {camOn && (
        <div className="mt-3 flex justify-center">
          <div id={scannerId} className="w-full max-w-md bg-black rounded-lg overflow-hidden" style={{ minHeight: 240 }} />
        </div>
      )}
    </div>
  );
}
